import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import {
  ASSETS_FIND_REPLACE_COLLECTION_QUERY,
  ASSETS_FIND_AND_REPLACE_MUTATION,
  PROJECT_PALLET_TYPES_QUERY,
} from "../../../constants/graphql";
import { useProjectInventory } from "../../../contexts/projectInventory";
import { useAssetsVariables } from "../../../contexts/assets-variables";
import useNotification from "../../../hooks/notification";
import { useBanner } from "../../../contexts/banner";
import useWizardStep from "src/hooks/useWizardStep";
import useQuery from "src/hooks/useQuery";
import useUpdateSearchParams from "src/hooks/useUpdateSearchParams";
import { fullWidthDialog } from "../../../constants/dialog";
import { EMPTY_STRING_SEARCH_TOKEN } from "../../../constants/search";

import Step0 from "./Step0";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import { convertDateFormat, normalizeLabel } from "../../AdvancedSearch/AdvancedSearchCriteria";

const QUERY_PARAM = "findReplace";
const STEP_VALUES = ["0", "1", "2", "3"];
const STEP_VALUES_BEFORE_DONE = STEP_VALUES.slice(0, 3);
const TIMESTAMP_FIELDS = ["created_at", "updated_at", "last_in", "last_out"];
const PARAM_FIELD = "frField";
const PARAM_OPERATOR = "frOp";
const PARAM_FIND = "frFind";
const PARAM_DONE = "frDone";
const CRITERIA_PARAMS = [PARAM_FIELD, PARAM_OPERATOR, PARAM_FIND, PARAM_DONE];
const CUSTOM_FIELD_PATTERN = /^customField\d+$/;
const PALLET_TYPE_FIELD_NAMES = new Set(["palletType", "pallet_type"]);

const normalizeSearchFieldName = (fieldName, fields) => {
  if (!fieldName) return fieldName;
  if (CUSTOM_FIELD_PATTERN.test(fieldName)) {
    const field = fields?.find(f => f.id === fieldName);
    return normalizeLabel(field?.label || fieldName);
  }
  return fieldName.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
};

const isPalletTypeField = fieldName => PALLET_TYPE_FIELD_NAMES.has(fieldName);

export default function FindReplaceWizard({ open, fields }) {
  const { projectId } = useProjectInventory();
  const { variables: assetsVariables } = useAssetsVariables() ?? {};
  const { notifyError } = useNotification();
  const { displayBanner } = useBanner();
  const urlParams = useQuery();
  const updateParams = useUpdateSearchParams();
  const isReplaceComplete = urlParams.get(PARAM_DONE) === "1";

  const [fieldValue, setFieldValue] = useState(() => urlParams.get(PARAM_FIELD) || "");
  const [findText, setFindText] = useState(() => urlParams.get(PARAM_FIND) || "");
  const [searchOperator, setSearchOperator] = useState(() => urlParams.get(PARAM_OPERATOR) || ":");
  const [replaceFieldValue, setReplaceFieldValue] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [resultCount, setResultCount] = useState(0);

  const availableSteps = isReplaceComplete ? STEP_VALUES : STEP_VALUES_BEFORE_DONE;
  const currentStep = parseInt(useWizardStep(availableSteps, { param: QUERY_PARAM }), 10);

  const setStep = step => updateParams({ [QUERY_PARAM]: String(step) });

  const removeStepParam = () =>
    updateParams(
      Object.fromEntries([QUERY_PARAM, ...CRITERIA_PARAMS].map(param => [param, null]))
    );

  const [searchAssetsQuery, { loading: searchLoading }] = useLazyQuery(ASSETS_FIND_REPLACE_COLLECTION_QUERY, {
    fetchPolicy: "network-only",
  });
  const searchAssets = (options) =>
    searchAssetsQuery(options).then(({ data }) => {
      const results = data?.assetsCollection?.collection || [];
      const metadata = data?.assetsCollection?.metadata || {};
      setSearchResults(results);
      setResultCount(metadata.totalCount || results.length);
    });
  const [fetchProjectPalletTypes] = useLazyQuery(PROJECT_PALLET_TYPES_QUERY, {
    fetchPolicy: "network-only",
  });

  const [findAndReplace, { loading: replaceLoading }] = useMutation(
    ASSETS_FIND_AND_REPLACE_MUTATION,
    {
      onCompleted: data => {
        const response = data?.assetsFindAndReplace;
        const mutationErrors = response?.errors || [];
        if (mutationErrors.length > 0) {
          notifyError(mutationErrors[0].message || "Failed to update assets");
          return;
        }

        if (response?.success) {
          updateParams({ [QUERY_PARAM]: "3", [PARAM_DONE]: "1" });
        }
      },
      onError: error => {
        notifyError(error.message || "Failed to update assets");
      },
    }
  );

  const handleStart = () => setStep(1);

  const handleFindBack = () => setStep(0);

  const normalizeSearchValue = async () => {
    if (findText === EMPTY_STRING_SEARCH_TOKEN) return findText;
    if (!isPalletTypeField(fieldValue)) return findText;
    if (!projectId) {
      notifyError("Project is required to find by pallet type");
      return null;
    }

    try {
      const inputValue = findText.trim();
      const { data } = await fetchProjectPalletTypes({
        variables: { projectId },
      });
      const palletTypes = data?.project?.palletTypes || [];

      // Dropdown already sends the ID directly, so check by ID first
      const matchById = palletTypes.find(pt => pt?.id === inputValue);
      if (matchById) return matchById.id;

      // Fallback: match by name (for manual text input)
      const matchByName = palletTypes.find(pt => pt?.name?.toLowerCase() === inputValue.toLowerCase());
      if (matchByName) return matchByName.id;

      notifyError("The pallet type you inputted does not exist");
      return null;
    } catch (error) {
      notifyError(error.message || "Failed to load pallet types");
      return null;
    }
  };

  const executeSearch = (field, operator, rawValue) => {
    const searchField = normalizeSearchFieldName(field, fields);
    // Convert timestamp fields from MM/DD/YYYY to YYYY-MM-DD for backend
    const searchValue = TIMESTAMP_FIELDS.includes(searchField)
      ? convertDateFormat(rawValue)
      : rawValue;

    return searchAssets({
      variables: {
        search: `${searchField} ${operator} ${searchValue}`,
        projectId,
        // Mirror the table's inventory scope; a fixed value reported 0 matches
        // for rows visible behind the dialog.
        hideDepletedAssets: Boolean(assetsVariables?.hideDepletedAssets),
        type: "Asset",
      },
    });
  };

  // Restore step 2 results on a cold deep link; mount-only so a normal
  // handleFind -> step 2 transition doesn't fire a duplicate search.
  useEffect(() => {
    if (currentStep === 2 && fieldValue && findText) {
      executeSearch(fieldValue, searchOperator, findText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFind = async () => {
    const normalizedFindValue = await normalizeSearchValue();
    if (!normalizedFindValue) return;

    executeSearch(fieldValue, searchOperator, normalizedFindValue);
    updateParams({
      [QUERY_PARAM]: "2",
      [PARAM_FIELD]: fieldValue,
      [PARAM_OPERATOR]: searchOperator,
      [PARAM_FIND]: findText,
    });
  };

  const handleResultsBack = () => setStep(1);

  const handleReplace = () => {
    if (!fieldValue || !findText || !searchResults || searchResults.length === 0) return;

    displayBanner({ message: "Processing...", type: "find-and-replace-assets" });

    const assetIds = searchResults.map(asset => asset.id);
    findAndReplace({
      variables: {
        assetIds,
        fieldName: fieldValue,
        findText,
        replaceText,
        ...(replaceFieldValue && replaceFieldValue !== fieldValue && { replaceFieldName: replaceFieldValue }),
      },
    });
  };

  const handleOpenSearchTab = async () => {
    const normalizedFindValue = await normalizeSearchValue();
    if (!normalizedFindValue) return;

    const searchField = normalizeSearchFieldName(fieldValue, fields);
    const timestampFields = ['created_at', 'updated_at', 'last_in', 'last_out'];
    const searchValue = timestampFields.includes(searchField)
      ? convertDateFormat(normalizedFindValue)
      : normalizedFindValue;
    const searchQuery = `${searchField} ${searchOperator} ${searchValue}`;
    const currentPath = window.location.pathname;

    let targetPath = currentPath;
    if (!targetPath.includes("/assets") && !targetPath.includes("/capture-inventory")) {
      if (projectId) {
        targetPath = `/projects/${projectId}/assets`;
      }
    }

    const searchParams = new URLSearchParams();
    searchParams.set("search", searchQuery);
    window.open(`${targetPath}?${searchParams.toString()}`, "_blank");
  };

  const handleClose = () => {
    setFieldValue("");
    setFindText("");
    setSearchOperator(":");
    setReplaceFieldValue("");
    setReplaceText("");
    setSearchResults([]);
    setResultCount(0);
    removeStepParam();
  };

  const getFieldLabel = () => {
    if (!fields || !fieldValue) return fieldValue;
    const field = fields.find(f => f.id === fieldValue);
    return field ? field.label : fieldValue;
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      onClose={handleClose}
      sx={{
        ...fullWidthDialog,
        "& .MuiDialog-paper": { maxWidth: 500 },
      }}
    >
      <DialogContent sx={{ textAlign: "center", px: 3 }}>
        {currentStep === 0 && <Step0 onStart={handleStart} />}
        {currentStep === 1 && (
          <Step1
            fields={fields}
            fieldValue={fieldValue}
            findText={findText}
            searchOperator={searchOperator}
            onFieldChange={setFieldValue}
            onFindTextChange={setFindText}
            onSearchOperatorChange={setSearchOperator}
            onBack={handleFindBack}
            onNext={handleFind}
          />
        )}
        {currentStep === 2 && (
          <Step2
            fields={fields}
            fieldValue={fieldValue}
            fieldLabel={getFieldLabel()}
            findText={findText}
            searchOperator={searchOperator}
            replaceFieldValue={replaceFieldValue}
            onReplaceFieldChange={newField => { setReplaceFieldValue(newField); setReplaceText(""); }}
            replaceText={replaceText}
            onReplaceTextChange={setReplaceText}
            resultCount={resultCount}
            searchLoading={searchLoading}
            replaceLoading={replaceLoading}
            onBack={handleResultsBack}
            onReplace={handleReplace}
            onOpenSearchTab={handleOpenSearchTab}
          />
        )}
        {currentStep === 3 && <Step3 onClose={handleClose} />}
      </DialogContent>
    </Dialog>
  );
}
